"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { Check, ChevronDown, Globe } from "lucide-react";
import {
  localeName,
  localeShort,
  locales,
  parsePath,
  pathFor,
  type Locale,
} from "@/i18n/config";
import { saveLanguagePreference } from "@/i18n/preference";
import { cn } from "@/lib/cn";

/**
 * Seletor de idioma. Leva para a MESMA página no outro idioma e grava a
 * escolha num cookie, que o servidor (.htaccess) respeita nas próximas
 * visitas, por cima do idioma do navegador. Links comuns (<a>): a troca
 * recarrega a página, o que também troca o <html lang>.
 */
export function LanguageSwitcher({
  locale,
  label,
  className,
}: {
  locale: Locale;
  label: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  const { page } = parsePath(pathname ?? "/");

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls="seletor-idioma"
        aria-label={`${label}: ${localeName[locale]}`}
        className="inline-flex min-h-12 cursor-pointer items-center gap-1.5 rounded-full border-2 border-brand-200 bg-white px-3 text-base font-semibold text-brand-950 transition-colors hover:border-brand-600"
      >
        <Globe aria-hidden className="size-5" />
        {localeShort[locale]}
        <ChevronDown
          aria-hidden
          className={cn("size-4 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <ul
          id="seletor-idioma"
          className="absolute right-0 z-50 mt-2 w-52 overflow-hidden rounded-2xl border border-brand-100 bg-white py-2 shadow-lift"
        >
          {locales.map((l) => {
            const current = l === locale;
            return (
              <li key={l}>
                <a
                  href={pathFor(l, page)}
                  hrefLang={l === "pt" ? "pt-BR" : l}
                  lang={l === "pt" ? "pt-BR" : l}
                  aria-current={current ? "true" : undefined}
                  onClick={() => saveLanguagePreference(l)}
                  className={cn(
                    "flex min-h-12 items-center justify-between gap-3 px-4 text-lg text-brand-950 hover:bg-brand-50",
                    current && "font-semibold",
                  )}
                >
                  <span>
                    <span className="mr-2 text-sm font-semibold text-brand-600">
                      {localeShort[l]}
                    </span>
                    {localeName[l]}
                  </span>
                  {current && <Check aria-hidden className="size-5 text-brand-600" />}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
